
import React, { useState, useEffect } from 'react';
import { EmergencyReportsList } from './components/EmergencyReportsList';
import { PatrolObservationsList } from './components/PatrolObservationsList';
import { ScheduleLogsList } from './components/ScheduleLogsList';
import { EarlyClockOutsList } from './components/EarlyClockOutsList';
import { ProblematicClockInsList } from './components/ProblematicClockInsList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, Eye, Calendar, Shield, Activity, TrendingUp, Clock, ClockAlert, Watch } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';

const Alerts = () => {
  const { isAdmin, isSuperAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const { isEnabled: isModuleEnabled } = useModuleAccess();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // Check if sentinel module is enabled
  const isSentinelEnabled = isModuleEnabled('sentinel');
  
  // Set default tab based on what's available
  const [activeTab, setActiveTab] = useState(isSentinelEnabled ? 'emergency' : 'schedule');
  
  // Update active tab if sentinel gets disabled and current tab is not available
  useEffect(() => {
    if (!isSentinelEnabled && (activeTab === 'emergency' || activeTab === 'observations')) {
      setActiveTab('schedule');
    }
  }, [isSentinelEnabled, activeTab]);

  if (isLoadingRoles) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-lg font-medium text-muted-foreground">Loading alerts dashboard...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex items-center justify-center">
          <Card className="max-w-md mx-auto bg-gradient-to-br from-red-50 to-red-100 border-red-200 shadow-luxury">
            <CardContent className="p-8 text-center">
              <div className="flex flex-col items-center space-y-6">
                <div className="p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg">
                  <Shield className="h-12 w-12 text-white" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-red-800">Access Restricted</h2>
                  <p className="text-red-600 leading-relaxed">This area is only accessible to administrators.</p>
                </div>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline" 
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Return to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Filter tab options based on sentinel module and user role
  const tabOptions = [
    ...(isSentinelEnabled ? [
      { value: 'emergency', label: 'Emergency Reports', icon: AlertTriangle },
      { value: 'observations', label: 'Patrol Observations', icon: Eye }
    ] : []),
    { value: 'schedule', label: 'Schedule Changes', icon: Calendar },
    ...((isAdmin || isSuperAdmin) ? [
      { value: 'early-clockouts', label: 'Early Clock Outs', icon: ClockAlert },
      { value: 'problematic-clockins', label: 'Problematic Clock Ins', icon: Watch }
    ] : [])
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 relative overflow-hidden">
        {/* Enhanced background patterns */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-red-500/5 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-red-500/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-radial from-primary/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Enhanced Header Section */}
          <div className="space-y-6">
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2 hover-scale bg-background/80 backdrop-blur-sm border-primary/20 hover:border-primary/40 transition-all duration-300"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-luxury animate-pulse">
                  <AlertTriangle className="h-8 w-8 text-white" />
                </div>
                <div className="h-8 w-px bg-gradient-to-b from-transparent via-primary/50 to-transparent"></div>
                <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-luxury animate-pulse" style={{ animationDelay: '0.5s' }}>
                  <Activity className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-red-600 via-red-500 to-primary bg-clip-text text-transparent leading-tight">
                Alerts Dashboard
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto font-medium">
                Monitor and manage security alerts, incidents, and system notifications
              </p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-red-500/20 shadow-sm">
                  <TrendingUp className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">Real-time Monitoring</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20 shadow-sm">
                  <Activity className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Active Surveillance</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-red-500/10 backdrop-blur-sm rounded-full border border-primary/30 shadow-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">24/7 Coverage</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Tabs Section */}
          {isMobile ? (
            <div className="space-y-6">
              <Card className="bg-gradient-to-r from-background/95 to-muted/50 border-primary/20 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-center text-lg font-semibold">Select Alert Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={activeTab} onValueChange={setActiveTab}>
                    <SelectTrigger className="w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-primary/30 hover:border-primary/50 transition-colors">
                      <SelectValue placeholder="Select alert type" />
                    </SelectTrigger>
                    <SelectContent>
                      {tabOptions.map((option) => {
                        const IconComponent = option.icon;
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <IconComponent className="h-4 w-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
              
              <div className="animate-fadeIn">
                {isSentinelEnabled && activeTab === 'emergency' && <EmergencyReportsList />}
                {isSentinelEnabled && activeTab === 'observations' && <PatrolObservationsList />}
                {activeTab === 'schedule' && <ScheduleLogsList />}
                {(isAdmin || isSuperAdmin) && activeTab === 'early-clockouts' && <EarlyClockOutsList />}
                {(isAdmin || isSuperAdmin) && activeTab === 'problematic-clockins' && <ProblematicClockInsList />}
              </div>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="flex justify-center mb-8">
                <TabsList className={`grid ${isSentinelEnabled ? (isAdmin || isSuperAdmin ? 'grid-cols-5' : 'grid-cols-3') : (isAdmin || isSuperAdmin ? 'grid-cols-3' : 'grid-cols-1')} bg-gradient-to-r from-muted/80 to-muted/60 backdrop-blur-sm border border-primary/20 shadow-lg rounded-xl p-2 h-14`}>
                  {isSentinelEnabled && (
                    <TabsTrigger 
                      value="emergency" 
                      className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <span className="hidden sm:inline">Emergency Reports</span>
                      <span className="sm:hidden">Emergency</span>
                    </TabsTrigger>
                  )}
                  {isSentinelEnabled && (
                    <TabsTrigger 
                      value="observations" 
                      className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg"
                    >
                      <Eye className="h-4 w-4" />
                      <span className="hidden sm:inline">Patrol Observations</span>
                      <span className="sm:hidden">Observations</span>
                    </TabsTrigger>
                  )}
                  <TabsTrigger 
                    value="schedule" 
                    className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg"
                  >
                    <Calendar className="h-4 w-4" />
                    <span className="hidden sm:inline">Schedule Changes</span>
                    <span className="sm:hidden">Schedule</span>
                  </TabsTrigger>
                  {(isAdmin || isSuperAdmin) && (
                    <TabsTrigger 
                      value="early-clockouts" 
                      className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg"
                    >
                      <ClockAlert className="h-4 w-4" />
                      <span className="hidden sm:inline">Early Clock Outs</span>
                      <span className="sm:hidden">Clock Outs</span>
                    </TabsTrigger>
                  )}
                  {(isAdmin || isSuperAdmin) && (
                    <TabsTrigger 
                      value="problematic-clockins" 
                      className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg"
                    >
                      <Watch className="h-4 w-4" />
                      <span className="hidden sm:inline">Problematic Clock Ins</span>
                      <span className="sm:hidden">Clock Ins</span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {isSentinelEnabled && (
                <TabsContent value="emergency" className="animate-fadeIn">
                  <EmergencyReportsList />
                </TabsContent>
              )}

              {isSentinelEnabled && (
                <TabsContent value="observations" className="animate-fadeIn">
                  <PatrolObservationsList />
                </TabsContent>
              )}

              <TabsContent value="schedule" className="animate-fadeIn">
                <ScheduleLogsList />
              </TabsContent>

              {(isAdmin || isSuperAdmin) && (
                <TabsContent value="early-clockouts" className="animate-fadeIn">
                  <EarlyClockOutsList />
                </TabsContent>
              )}

              {(isAdmin || isSuperAdmin) && (
                <TabsContent value="problematic-clockins" className="animate-fadeIn">
                  <ProblematicClockInsList />
                </TabsContent>
              )}
            </Tabs>
          )}
          
          {/* Enhanced Footer */}
          <footer className="text-center py-8 mt-16">
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span>Security Monitoring</span>
              </div>
              <div className="h-4 w-px bg-muted-foreground/30"></div>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-500 animate-pulse" />
                <span>System Online</span>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </AppLayout>
  );
};

export default Alerts;
